/**
 * Händler-Portal (GDD §1 "Händler-Relaisstation", §4 offer card).
 *
 * Two ways to trade, both gated behind Händler-Relaisstation level ≥ 1:
 *  - **Standard market:** sell raw resources for credits, or buy them back at
 *    a merchant margin. Both directions are taxed; the tax falls with
 *    Logistik-&-Handelsverträge research and the economy talent band
 *    (GDD §1 research table, §2 talent tier 6–10).
 *  - **Rotating offers:** bulk deals below the standard buy price, refreshed
 *    every 4 h. This is the card the GDD wireframe shows.
 *
 * Credits only ever enter the economy through selling and missions, and leave
 * through buying and the casino — keeping the credit sink loop of GDD §4 intact.
 */

import type {
  GameState,
  MarketState,
  TradeOffer,
  TradeableResource,
} from './types.js';
import { merchantTax } from './formulas.js';
import { talentEffects } from './data/talents.js';
import { nextInt, nextRandom } from './rng.js';

/** Base value of one unit in Galactic Credits (design fill-in). */
export const MARKET_RATES: Record<TradeableResource, number> = {
  titan: 0.01, // 100 Titan → 1 Credit
  silicate: 0.02, // scarcer: worth double
  isotope: 0.06, // fuel, scarcest of the three
};

/** Merchant margin between the sell and buy side. */
export const MARKET_SPREAD = 0.25;

/** How often the offer board rotates. */
export const OFFER_REFRESH_MS = 4 * 3_600_000;
const OFFERS_PER_ROTATION = 3;

export const TRADEABLE: TradeableResource[] = ['titan', 'silicate', 'isotope'];

/**
 * German display names. Engine-emitted event messages are shown verbatim in
 * the UI, so they must never leak raw state keys like "titan" at the player.
 */
export const RESOURCE_LABELS: Record<TradeableResource, string> = {
  titan: 'Titan-Legierung',
  silicate: 'Kristall-Silikat',
  isotope: 'Isotop-3',
};

/** Effective tax: research reduction plus the economy talent band (GDD §2). */
export function effectiveTax(state: GameState): number {
  const talents = talentEffects(state.talents);
  return Math.max(0.01, merchantTax(state.research.logistics) - talents.taxReduction);
}

/** Credits received for selling `amount` of `resource`, after tax. */
export function sellValue(state: GameState, resource: TradeableResource, amount: number): number {
  const gross = amount * MARKET_RATES[resource];
  return Math.floor(gross * (1 - effectiveTax(state)));
}

/** Credits required to buy `amount` of `resource`, including margin and tax. */
export function buyPrice(state: GameState, resource: TradeableResource, amount: number): number {
  const gross = amount * MARKET_RATES[resource] * (1 + MARKET_SPREAD);
  return Math.ceil(gross * (1 + effectiveTax(state)));
}

/** Empty board for a fresh game. */
export function createMarket(now: number): MarketState {
  return { offers: [], refreshAt: now };
}

/**
 * Roll a new offer board. Mutates `state` (rngSeed, nextId, market).
 *
 * Offer sizes scale with account level so the board stays relevant: a level-40
 * commander is not moved by 10 000 Titan.
 */
export function refreshOffers(state: GameState, now: number): void {
  const offers: TradeOffer[] = [];
  const scale = 1 + state.level * 0.35;

  for (let i = 0; i < OFFERS_PER_ROTATION; i++) {
    const pick = nextInt(state.rngSeed, 0, TRADEABLE.length - 1);
    state.rngSeed = pick.seed;
    const resource = TRADEABLE[pick.value]!;

    const sizeRoll = nextInt(state.rngSeed, 5_000, 15_000);
    state.rngSeed = sizeRoll.seed;
    // Isotope trades in smaller lots — it is worth 6× titan per unit.
    const lotFactor = resource === 'isotope' ? 0.2 : resource === 'silicate' ? 0.6 : 1;
    const amount = Math.round(sizeRoll.value * scale * lotFactor);

    const discountRoll = nextRandom(state.rngSeed);
    state.rngSeed = discountRoll.seed;
    const discount = 0.1 + discountRoll.value * 0.25; // 10–35 % below buy price

    offers.push({
      id: state.nextId++,
      resource,
      amount,
      priceCredits: Math.max(1, Math.ceil(buyPrice(state, resource, amount) * (1 - discount))),
      discount,
      expiresAt: now + OFFER_REFRESH_MS,
    });
  }

  state.market = { offers, refreshAt: now + OFFER_REFRESH_MS };
}

/** True once the Händler-Relaisstation is online (GDD §1). */
export function marketUnlocked(state: GameState): boolean {
  return state.planets[0]!.buildings.tradeRelay >= 1;
}
